/**
 * useCalendarEvents Hook
 * Processes and formats calendar events for display
 */

import { useMemo } from 'react';
import { geneRateEarningsArray } from '../../CdpPay/functions';
import { processProviderPayData, flattenCalendarList, getContractType, getPayBillablePercentage } from '../services/payDataTransformService';
import { processHygienistData, generateDateKeyObjects } from '../services/hygienistDataService';
import { generateCalendarPayList } from '../services/calendarEventService';
import { getDoctorDetailsByAbbr } from '../services/doctorDataService';
import { dentistTypes } from '../../../constants';

/**
 * Custom hook for processing calendar events
 * @param {Object} payData - Raw pay data from Redux
 * @param {Object} user - Current user object
 * @returns {Object} { calendarEvents, providerPayByDoctor, earningsArray, hygData, dateKeyObjects, contractType, payBillable }
 */
export const useCalendarEvents = (payData, user) => {
  // Memoize processed data to avoid recalculations on every render
  const processedData = useMemo(() => {
    if (!payData || !user?.UdaDrList?.abbr) {
      return {
        providerPayByDoctor: {},
        earningsArray: [],
        contractType: null,
        providerCalendarList: [],
        hygData: [],
        dateKeyObjects: {},
        payBillable: 0,
        calendarEvents: []
      };
    }

    // Extract and process provider pay data
    const providerPayByDoctor = processProviderPayData(payData, user.UdaDrList.abbr);

    // Get total earnings
    const earningsArray = geneRateEarningsArray(providerPayByDoctor);

    // Get contract type and pay billable percentage
    const contractType = getContractType(providerPayByDoctor);
    const payBillable = getPayBillablePercentage(providerPayByDoctor);

    // Flatten calendar list from earnings
    const providerCalendarList = flattenCalendarList(earningsArray);

    // Process hygienist data
    const hygData = processHygienistData(earningsArray, contractType);
    const dateKeyObjects = generateDateKeyObjects(hygData);

    // Get doctor details for contract name
    const doctorDetails = getDoctorDetailsByAbbr(user, dentistTypes, user.UdaDrList.abbr);
    const doctorContractType = doctorDetails?.contractName ?? contractType;

    // Generate calendar events with proper formatting
    const calendarEvents = generateCalendarPayList(
      providerCalendarList,
      doctorContractType,
      dateKeyObjects,
      payBillable
    );

    return {
      providerPayByDoctor,
      earningsArray,
      contractType,
      providerCalendarList,
      hygData,
      dateKeyObjects,
      payBillable,
      calendarEvents
    };
  }, [payData, user]);

  return processedData;
};
